import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * Blog grid slider component.
 *
 * On mobile, restructures the blog grid so the featured (first) card
 * appears on top and the remaining cards become a horizontally
 * scrollable/draggable slider. On desktop, the original grid layout
 * is restored.
 *
 * @extends {HTMLElement}
 */
class BlogGridSlider extends HTMLElement {
  /** @type {boolean} */
  #isMobile = false;

  /** @type {HTMLElement[]} */
  #sliderCards = [];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.#isMobile = isMobileBreakpoint();
    this.#layout();
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  #layout() {
    const grid = this.querySelector('.sl-blog-grid');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > .sl-blog-grid-card'));
    if (cards.length <= 1) return;

    if (isMobileBreakpoint()) {
      this.#setupMobileLayout(grid, cards);
    } else {
      this.#teardownMobileLayout(grid);
    }
  }

  /**
   * @param {HTMLElement} grid
   * @param {HTMLElement[]} cards
   */
  #setupMobileLayout(grid, cards) {
    if (grid.querySelector('.sl-blog-grid__slider-track')) return;

    const rest = cards.slice(1);

    grid.classList.add('sl-blog-grid--mobile');

    const track = document.createElement('div');
    track.classList.add('sl-blog-grid__slider-track');

    rest.forEach((card) => track.appendChild(card));
    grid.appendChild(track);

    this.#sliderCards = rest;
  }

  /**
   * @param {HTMLElement} grid
   */
  #teardownMobileLayout(grid) {
    const track = grid.querySelector('.sl-blog-grid__slider-track');
    if (!track) return;

    const trackCards = Array.from(track.querySelectorAll(':scope > .sl-blog-grid-card'));
    trackCards.forEach((card) => grid.appendChild(card));
    track.remove();

    grid.classList.remove('sl-blog-grid--mobile');
    this.#sliderCards = [];
  }

  /** @param {ResizeObserverEntry[]} _entries */
  #handleResize = () => {
    const nowMobile = isMobileBreakpoint();
    if (nowMobile !== this.#isMobile) {
      this.#isMobile = nowMobile;
      this.#layout();
    }
  };

  /** @param {MediaQueryListEvent} event */
  #handleBreakpointChange = (event) => {
    const nowMobile = !event.matches;
    if (nowMobile !== this.#isMobile) {
      this.#isMobile = nowMobile;
      this.#layout();
    }
  };
}

if (!customElements.get('blog-grid-slider')) {
  customElements.define('blog-grid-slider', BlogGridSlider);
}